import { router, usePage } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_DELAY_MS = 15;

export default function GlobalRedirectLoader({ delay = DEFAULT_DELAY_MS } = {}) {
    // Initialize hooks first to ensure hook call order is stable across renders.
    // Avoid returning before hooks are declared — returning early here previously
    // caused React's "Rendered fewer hooks than expected" when sometimes the
    // component returned before calling its hooks.
    const [isVisible, setIsVisible] = useState(false);
    const isVisibleRef = useRef(isVisible);

    useEffect(() => {
        isVisibleRef.current = isVisible;
    }, [isVisible]);

    useEffect(() => {
        let pendingNavigations = 0;
        let showTimeoutId = null;

        const scheduleShow = () => {
            if (showTimeoutId !== null || isVisibleRef.current) {
                return;
            }
            showTimeoutId = window.setTimeout(() => {
                showTimeoutId = null;
                setIsVisible(true);
            }, delay);
        };

        const hideLoader = () => {
            if (showTimeoutId !== null) {
                window.clearTimeout(showTimeoutId);
                showTimeoutId = null;
            }
            setIsVisible(false);
        };

        const handleStart = (event) => {
            const method = event?.detail?.visit?.method?.toLowerCase?.();
            if (method && method !== 'get' ) {
                return;
            }

            // If the navigation target is within the test-taking flow (/tests/{test}/question/{question}),
            // suppress the global redirect loader. Use URL parsing of the visit URL (or current origin) so
            // variants like relative URLs or URLs without trailing slashes are handled reliably.
            try {
                const rawUrl = event?.detail?.visit?.url || '';
                // NOTE: suppressed global redirect loader for test-taking routes
                if (typeof rawUrl === 'string' && rawUrl.length > 0) {
                    // Ensure we can parse relative URLs by providing a base origin
                    let parsed;
                    try {
                        parsed = new URL(rawUrl, window.location.origin);
                    } catch (e) {
                        parsed = null;
                    }

                    if (parsed && typeof parsed.pathname === 'string') {
                        const pathname = parsed.pathname;
                        // Normalize and check for /tests/ or /tests/{test}/question/{question}
                        if (pathname.toLowerCase().startsWith('/tests/') || /\/tests\/.+\/question\/.+/i.test(pathname)) {
                            // Ensure any previously-scheduled or visible loader is hidden for test navigations
                            hideLoader();
                            return;
                        }
                    } else if (rawUrl.toLowerCase().includes('/tests/')) {
                        // Fallback: simple substring check
                        // Fallback: simple substring check
                        hideLoader();
                        return;
                    }
                }
            } catch (e) {
                // fall through and show loader
            }

            pendingNavigations += 1;
            scheduleShow();
        };

        const handleFinish = () => {
            if (pendingNavigations === 0) {
                return;
            }
            pendingNavigations = Math.max(0, pendingNavigations - 1);
            if (pendingNavigations === 0) {
                hideLoader();
            }
        };

        const handleCancel = () => {
            if (pendingNavigations === 0) {
                return;
            }
            pendingNavigations = Math.max(0, pendingNavigations - 1);
            if (pendingNavigations === 0) {
                hideLoader();
            }
        };

        const stopListeningStart = router.on('start', handleStart);
        const stopListeningFinish = router.on('finish', handleFinish);
        const stopListeningCancel = router.on('cancel', handleCancel);

        return () => {
            pendingNavigations = 0;
            if (showTimeoutId !== null) {
                window.clearTimeout(showTimeoutId);
            }
            stopListeningStart?.();
            stopListeningFinish?.();
            stopListeningCancel?.();
        };
    }, [delay]);

    // After hooks are declared and effects scheduled, compute the short-circuit
    // condition. This keeps hooks order stable while still avoiding render of the
    // loader UI on test-taking routes.
    let portalTarget = null;
    try {
        // Quick short-circuit: if current page is inside the test-taking flow, don't render this global loader
        // (defensive, handles cases where Inertia navigations still cause the overlay to flash).
        if (typeof window !== 'undefined' && typeof window.location?.pathname === 'string') {
            const currentPath = window.location.pathname.toLowerCase();
            if (currentPath.startsWith('/tests/')) {
                // Component will render nothing on test pages
                return null;
            }
        }
    } catch (e) {
        // ignore and continue
    }

    portalTarget = typeof document !== 'undefined'
        ? document.getElementById('app') || document.querySelector('.app-root') || document.body
        : null;

    if (!isVisible || !portalTarget) {
        return null;
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[1200] flex items-center justify-center bg-gray-900/60 backdrop-blur"
            aria-hidden={!isVisible}
        >
            <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-3 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-700 shadow-lg dark:bg-gray-800/90 dark:text-gray-100"
            >
                <svg
                    className="h-5 w-5 animate-spin text-primary-600"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 017-7.938V2a10 10 0 00-9 9h2z"
                    />
                </svg>
                <span>Redirigiendo...</span>
            </div>
        </div>,
        portalTarget,
    );
}
